import { tShared } from "@/shared/i18n";

/**
 * Мапа кодов ошибок с backend на понятные сообщения для пользователей.
 *
 * Русские строки здесь это значения по умолчанию: partner-кабинет берёт их из
 * словаря по ключам `shared.errors.*` (сид
 * `backend/src/migrations/data/partnerSeed/sharedComponents.ts`), а админка,
 * у которой этих ключей нет, показывает текст отсюда.
 */
export const ERROR_CODE_MESSAGES: Record<string, string> = {
  // Auth errors - ошибки авторизации
  AUTH_INVALID_CODE: "Неверный код подтверждения",
  AUTH_CODE_EXPIRED: "Код истек. Запросите новый код",
  AUTH_CODE_ALREADY_USED: "Код уже был использован. Запросите новый код",
  AUTH_CODE_NOT_FOUND: "Не верный код",
  AUTH_INVALID_TOKEN: "Неверный токен. Попробуйте войти заново",
  AUTH_TOKEN_EXPIRED: "Токен истек. Попробуйте войти заново",
  AUTH_TOKEN_MISMATCH: "Неверный токен. Попробуйте войти заново",
  AUTH_VERIFICATION_FAILED: "Ошибка проверки кода. Попробуйте еще раз",
  AUTH_UNAUTHORIZED: "Необходима авторизация",

  // User errors - ошибки пользователей
  USER_NOT_FOUND: "Пользователь не найден",
  USER_EMAIL_ALREADY_EXISTS: "Этот email уже используется другим пользователем",
  USER_PHONE_ALREADY_EXISTS:
    "Этот номер телефона уже используется другим пользователем",
  USER_INVALID_EMAIL_FORMAT: "Неверный формат email",
  USER_INVALID_PHONE_FORMAT: "Неверный формат номера телефона",

  // Email errors - ошибки Email
  EMAIL_SEND_FAILED: "Не удалось отправить email. Попробуйте войти через SMS",
  EMAIL_SERVICE_UNAVAILABLE:
    "Email сервис временно недоступен. Попробуйте войти через SMS",
  EMAIL_RATE_LIMIT_EXCEEDED: "Превышен лимит отправки писем. Попробуйте позже",

  // Organization user errors - ошибки сотрудников организации (раздел
  // «Сотрудники» партнёрского кабинета)
  ORGANIZATION_USER_EXISTS: "Пользователь уже связан с данной организацией",
  LAST_ORGANIZATION_ADMIN:
    "В организации должен остаться хотя бы один администратор",
  CANNOT_DISABLE_SELF: "Нельзя отключить самого себя",
  INVITATION_RATE_LIMIT:
    "Приглашение уже отправлено, повторить можно через несколько минут",
  ORGANIZATION_USER_DISABLED: "Нельзя изменить роли отключённого сотрудника",

  // Биллинг ottkit: контракты, платежи по ним и отчёты доли с продаж
  REVENUE_SHARE_CONTRACT_EXISTS:
    "У организации уже есть действующий контракт с долей с продаж",
  CONTRACT_DATE_TO_REQUIRED:
    "Для лицензии и разовых работ нужна дата окончания контракта",
  CONTRACT_DATE_RANGE_INVALID:
    "Дата окончания контракта не может быть раньше даты начала",
  CONTRACT_ITEMS_REQUIRED: "В контракте должна быть хотя бы одна строка услуги",
  CONTRACT_ITEM_AMOUNT_INVALID:
    "У лицензии и разовой работы нужна положительная сумма, у доли с продаж суммы быть не должно",
  CONTRACT_ALREADY_CANCELED: "Контракт уже отменён",
  CONTRACT_PAYMENT_TRANSITION_INVALID: "Такой переход статуса платежа запрещён",
  CONTRACT_PAYMENT_ALREADY_EXISTS:
    "На эту дату инвойса у контракта уже есть платёж, который нельзя пересобрать",
  REVENUE_SHARE_REPORT_NOT_EDITABLE:
    "Отчёт закрыт и оплачен, корректировку добавить нельзя",
  REVENUE_SHARE_REPORT_NOT_DRAFT: "Пересчитать можно только черновик отчёта",
  BILLING_SELLER_DUPLICATE_BANK_CURRENCY:
    "Банковская строка для этой валюты уже есть, у каждой валюты может быть только одна строка",
  BILLING_SELLER_NOT_FILLED:
    "Заполните реквизиты продавца, иначе счета выставить нельзя",
  BILLING_SELLER_BANK_ACCOUNT_MISSING:
    "У продавца нет банковской строки для валюты счёта",
  ORGANIZATION_BILLING_REQUISITES_NOT_FILLED:
    "У организации не заполнены реквизиты плательщика, иначе счёт выставить нельзя",
  CONTRACT_PAYMENT_INVOICE_NOT_ISSUABLE:
    "Счёт по этому платежу выставить нельзя",
  CONTRACT_PAYMENT_INVOICE_NOT_REISSUABLE:
    "Счёт по этому платежу перевыставить нельзя",
  CONTRACT_PAYMENT_INVOICE_MISSING: "По этому платежу ещё не выставлен счёт",
  CONTRACT_PAYMENT_INVOICE_CURRENCY_INCONSISTENT:
    "У платежа расходятся сумма и курс счёта, перевыставить его нельзя. Нужна проверка данных",
  CONTRACT_PAYMENT_INVOICE_NOT_SENDABLE:
    "Письмо нельзя отправить: счёт уже оплачен или отменён",
  CONTRACT_PAYMENT_INVOICE_NO_RECIPIENTS:
    "У организации не заполнены адреса для счёта, отправлять некуда",
  CONTRACT_PAYMENT_INVOICE_MAIL_FAILED:
    "Часть писем не отправилась, попробуйте отправить письмо повторно",
  ORGANIZATION_BILLING_EMAIL_INVALID:
    "Один из адресов в реквизитах плательщика указан в неверном формате",
  // Четвёртая волна ревью, находка 2: раньше оба отказа приходили общими
  // кодами (REVENUE_SHARE_REPORT_NOT_EDITABLE и
  // CONTRACT_PAYMENT_ALREADY_EXISTS), и словарный текст врал про
  // оплаченный отчёт. Своя пара кодов честнее и не ломает общий приём
  // «текст по коду из словаря»: исключений в getErrorMessage не появилось.
  CONTRACT_PAYMENT_INVOICE_PAUSED:
    "Платёж приостановлен, а счёт по нему уже выставлен: верните платёж в ожидание оплаты, тогда правка пройдёт вместе с перевыставлением счёта",
  CONTRACT_PAYMENT_INVOICE_ISSUED:
    "По платежу на эту дату инвойса уже выставлен счёт: расписание по нему не пересобирается, иначе сумма платежа разойдётся с напечатанным счётом. Перевыставите счёт по этому платежу, а разницу за этот период проведите отдельным платежом",
  INVOICE_EXCHANGE_RATE_MISSING:
    "Нет курса ЕЦБ для пересчёта суммы в валюту счёта, счёт не выставлен. Дневная джоба повторит попытку, когда курс загрузится",
};

/**
 * Коды ошибок, при которых нужно показать кнопку "Получить новый код"
 */
export const RESEND_CODE_ERROR_CODES = [
  "AUTH_CODE_EXPIRED",
  "AUTH_CODE_ALREADY_USED",
];

/**
 * Достаёт код ошибки из ответа GraphQL, если он там есть
 */
export function getGraphQLErrorCode(error: any): string | undefined {
  return error?.graphQLErrors?.[0]?.extensions?.code;
}

/**
 * Получает понятное сообщение по коду ошибки
 */
export function getErrorMessage(
  errorCode?: string,
  fallbackMessage?: string
): string {
  if (errorCode && ERROR_CODE_MESSAGES[errorCode]) {
    return tShared(
      `shared.errors.${errorCode}`,
      ERROR_CODE_MESSAGES[errorCode]
    );
  }

  // fallbackMessage это исходный текст ошибки с бэкенда, он приходит уже
  // локализованным (или не локализованным) сервером и здесь не переводится.
  return (
    fallbackMessage ||
    tShared("shared.errors.default", "Произошла ошибка. Попробуйте еще раз")
  );
}

/**
 * Проверяет, нужно ли показать кнопку "Получить новый код"
 */
export function shouldShowResendButton(errorCode?: string): boolean {
  return errorCode ? RESEND_CODE_ERROR_CODES.includes(errorCode) : false;
}

/**
 * Convenience function for handling authentication errors
 * Uses default options for auth operations
 */
export function handleAuthError(error: any): {
  message: string;
  shouldShowResend: boolean;
} {
  // For simple error processing based only on error codes
  const errorCode = getGraphQLErrorCode(error);
  if (errorCode) {
    return {
      message: getErrorMessage(errorCode, error.graphQLErrors[0].message),
      shouldShowResend: shouldShowResendButton(errorCode),
    };
  }

  // Fallback for non-coded errors
  const message =
    error?.message ||
    error?.graphQLErrors?.[0]?.message ||
    tShared("shared.errors.auth", "Произошла ошибка при авторизации");
  return {
    message,
    shouldShowResend: false,
  };
}
